import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FaAddressCard,
  FaBell,
  FaChevronLeft,
  FaCog,
  FaEnvelope,
  FaHandshake,
  FaHome,
  FaImages,
  FaMoon,
  FaNewspaper,
  FaSun,
  FaUsers,
} from "react-icons/fa";
import { FaBuildingUser } from "react-icons/fa6";
import { GiKimono } from "react-icons/gi";
import { MdDashboard } from "react-icons/md";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

const DEFAULT_LOGO =
  "data:image/svg+xml,%3Csvg width='120' height='120' viewBox='0 0 120 120' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='120' height='120' rx='30' fill='%230f172a'/%3E%3Ctext x='60' y='70' text-anchor='middle' font-size='42' font-family='Arial' font-weight='700' fill='%23facc15'%3EM%3C/text%3E%3C/svg%3E";

const DEFAULT_AVATAR =
  "data:image/svg+xml,%3Csvg width='120' height='120' viewBox='0 0 120 120' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='120' height='120' rx='60' fill='%23e2e8f0'/%3E%3Ccircle cx='60' cy='46' r='22' fill='%2394a3b8'/%3E%3Cpath d='M24 106c6-24 20-36 36-36s30 12 36 36' fill='%2394a3b8'/%3E%3C/svg%3E";

const isExternalURL = (url) => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

const getMediaUrl = (url, fallback) => {
  if (!url) return fallback;
  if (isExternalURL(url)) return url;
  return `${BASE_URL}${url.startsWith("/") ? url : `/${url}`}`;
};

const getRoleName = (roleId) => {
  switch (Number(roleId)) {
    case 1:
      return "Administrateur";
    case 2:
      return "Bureau";
    case 3:
      return "Enseignant";
    default:
      return "Utilisateur";
  }
};

const MENU_ITEMS = [
  {
    title: "Tableau de bord",
    path: "/appli/dashboard",
    icon: MdDashboard,
  },
  {
    title: "Inscriptions",
    path: "/appli/inscriptions",
    icon: FaAddressCard,
  },
  {
    title: "Disciplines",
    path: "/appli/disciplines",
    icon: GiKimono,
  },
  {
    title: "Actualités",
    path: "/appli/actualites",
    icon: FaNewspaper,
  },
  {
    title: "Albums photos",
    path: "/appli/albums-photos",
    icon: FaImages,
  },
  {
    title: "Partenaires",
    path: "/appli/partenaires",
    icon: FaHandshake,
  },
  {
    title: "Groupes",
    path: "/appli/groupes",
    icon: FaUsers,
  },
  {
    title: "Le Club",
    path: "/appli/le-club",
    icon: FaBuildingUser,
  },
  {
    title: "Paramètres",
    path: "/appli/parametres",
    icon: FaCog,
  },
];

const BADGE_ITEMS = [
  {
    title: "Notifications",
    path: "/appli/notifications",
    icon: FaBell,
    key: "notifications",
  },
  {
    title: "Messages",
    path: "/appli/messages-contact",
    icon: FaEnvelope,
    key: "messages",
  },
];

export default function AsideMenuAdmin({
  isOpen,
  toggleMenu,
  clubInfo,
  userInfo,
  isDarkMode,
  onToggleTheme,
  unreadCounts = {
    notifications: 0,
    messages: 0,
  },
}) {
  const location = useLocation();

  const logoUrl = useMemo(
    () => getMediaUrl(clubInfo?.logo, DEFAULT_LOGO),
    [clubInfo?.logo]
  );

  const avatarUrl = useMemo(
    () => getMediaUrl(userInfo?.photoURL, DEFAULT_AVATAR),
    [userInfo?.photoURL]
  );

  const userFullName = useMemo(() => {
    const firstname = userInfo?.prenom || "";
    const lastname = userInfo?.nom || "";
    const fullName = `${firstname} ${lastname}`.trim();

    return fullName || "Administrateur";
  }, [userInfo?.nom, userInfo?.prenom]);

  const isActivePath = (path) => {
    if (path === "/appli/dashboard") {
      return (
        location.pathname === "/appli" ||
        location.pathname === "/appli/" ||
        location.pathname === path
      );
    }

    return location.pathname.startsWith(path);
  };

  return (
    <aside
      className={`fixed inset-y-0 left-0 z-40 flex h-screen flex-col border-r border-white/10 bg-slate-950 text-white shadow-2xl shadow-slate-950/40 transition-all duration-300 dark:border-slate-800 ${
        isOpen
          ? "w-72 translate-x-0"
          : "-translate-x-full lg:w-24 lg:translate-x-0"
      }`}
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="relative border-b border-white/10 px-4 py-5">
          <div
            className={`flex items-center gap-3 ${
              isOpen ? "justify-start" : "justify-center"
            }`}
          >
            <Link
              to="/appli/dashboard"
              className="flex min-w-0 items-center gap-3"
              title={clubInfo?.nom || "MonAppliClub Light"}
            >
              <span className="grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-2xl bg-white/10 ring-1 ring-white/10">
                <img
                  src={logoUrl}
                  alt={clubInfo?.nom || "Logo du club"}
                  className="h-full w-full object-cover"
                />
              </span>

              {isOpen && (
                <span className="min-w-0">
                  <span className="block truncate text-sm font-bold uppercase tracking-[0.22em] text-yellow-300">
                    Admin
                  </span>
                  <span className="mt-1 block truncate text-lg font-bold leading-tight text-white">
                    {clubInfo?.nom || "MonAppliClub Light"}
                  </span>
                </span>
              )}
            </Link>
          </div>

          <button
            type="button"
            onClick={toggleMenu}
            aria-label={isOpen ? "Réduire le menu" : "Ouvrir le menu"}
            className="absolute -right-4 top-8 hidden h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-lg transition hover:-translate-y-0.5 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800 lg:flex"
          >
            <FaChevronLeft
              className={`text-sm transition-transform duration-300 ${
                !isOpen ? "rotate-180" : ""
              }`}
            />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-3 py-4">
          <nav className="space-y-1">
            {MENU_ITEMS.map((item) => {
              const Icon = item.icon;
              const active = isActivePath(item.path);

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  title={!isOpen ? item.title : undefined}
                  className={`group relative flex items-center gap-3 rounded-2xl px-3 py-3 text-sm transition ${
                    active
                      ? "bg-white text-slate-950 shadow-lg shadow-black/20"
                      : "text-slate-300 hover:bg-white/10 hover:text-white"
                  } ${isOpen ? "justify-start" : "justify-center"}`}
                >
                  <span
                    className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl transition ${
                      active
                        ? "bg-gradient-to-br from-yellow-300 to-orange-400 text-slate-950"
                        : "bg-white/10 text-slate-200 group-hover:bg-white/15"
                    }`}
                  >
                    <Icon className="text-xl" />
                  </span>

                  {isOpen && (
                    <span className="min-w-0 flex-1 truncate font-medium">
                      {item.title}
                    </span>
                  )}

                  {active && isOpen && (
                    <span className="h-2 w-2 rounded-full bg-orange-400" />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* <div className="my-5 h-px bg-white/10" /> */}

          {/* <nav className="space-y-1">
            {BADGE_ITEMS.map((item) => {
              const Icon = item.icon;
              const count = Number(unreadCounts?.[item.key] || 0);
              const active = isActivePath(item.path);

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  title={!isOpen ? item.title : undefined}
                  className={`group relative flex items-center gap-3 rounded-2xl px-3 py-3 text-sm transition ${
                    active
                      ? "bg-white text-slate-950 shadow-lg shadow-black/20"
                      : "text-slate-300 hover:bg-white/10 hover:text-white"
                  } ${isOpen ? "justify-start" : "justify-center"}`}
                >
                  <span
                    className={`relative grid h-10 w-10 shrink-0 place-items-center rounded-xl transition ${
                      active
                        ? "bg-gradient-to-br from-yellow-300 to-orange-400 text-slate-950"
                        : "bg-white/10 text-slate-200 group-hover:bg-white/15"
                    }`}
                  >
                    <Icon className="text-lg" />

                    {count > 0 && (
                      <span className="absolute -right-1 -top-1 grid min-h-5 min-w-5 place-items-center rounded-full bg-rose-500 px-1.5 text-[10px] font-bold text-white ring-2 ring-slate-950">
                        {count > 99 ? "99+" : count}
                      </span>
                    )}
                  </span>

                  {isOpen && (
                    <>
                      <span className="min-w-0 flex-1 truncate font-medium">
                        {item.title}
                      </span>

                      {count > 0 && (
                        <span className="rounded-full bg-rose-500/15 px-2 py-1 text-xs font-bold text-rose-200">
                          {count > 99 ? "99+" : count}
                        </span>
                      )}
                    </>
                  )}
                </Link>
              );
            })}
          </nav> */}

          <div className="my-5 h-px bg-white/10" />

          <Link
            to="/"
            title={!isOpen ? "Accueil du site" : undefined}
            className={`group flex items-center gap-3 rounded-2xl px-3 py-3 text-sm text-slate-300 transition hover:bg-white/10 hover:text-white ${
              isOpen ? "justify-start" : "justify-center"
            }`}
          >
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white/10 text-slate-200 group-hover:bg-white/15">
              <FaHome className="text-lg" />
            </span>

            {isOpen && (
              <span className="min-w-0 flex-1 truncate font-medium">
                Accueil du site
              </span>
            )}
          </Link>

          <button
            type="button"
            onClick={onToggleTheme}
            title={!isOpen ? "Changer le thème" : undefined}
            className={`group mt-1 flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-sm text-slate-300 transition hover:bg-white/10 hover:text-white ${
              isOpen ? "justify-start" : "justify-center"
            }`}
          >
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white/10 text-slate-200 group-hover:bg-white/15">
              {isDarkMode ? (
                <FaSun className="text-lg text-yellow-300" />
              ) : (
                <FaMoon className="text-lg" />
              )}
            </span>

            {isOpen && (
              <span className="min-w-0 flex-1 truncate text-left font-medium">
                {isDarkMode ? "Mode clair" : "Mode sombre"}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* <div className="border-t border-white/10 p-3">
        <div
          className={`flex items-center gap-3 rounded-2xl bg-white/10 p-3 ${
            isOpen ? "justify-start" : "justify-center"
          }`}
        >
          <img
            src={avatarUrl}
            alt={userFullName}
            className="h-11 w-11 shrink-0 rounded-2xl object-cover ring-2 ring-white/10"
          />

          {isOpen && (
            <div className="min-w-0">
              <p className="truncate text-sm font-bold text-white">
                {userFullName}
              </p>
              <p className="mt-0.5 truncate text-xs text-slate-400">
                {getRoleName(userInfo?.role_id)}
              </p>
            </div>
          )}
        </div>
      </div> */}
    </aside>
  );
}